import type {Component, Editor} from 'grapesjs';
import type {PosterPageHashedIdInfo} from '@Libraries/poster-library';
import {convertComponentIfNeeded} from '@Libraries/email-campaigns-library';
import {getEnumMember} from '@Utils/string.util';
import {openErrorModal} from '@Modals/error-modal';
import {EMAIL_MAKER_BLOCKS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import type {AsyncGetPreviewSuccessResponse} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {AsyncGetPreviewErrorCodes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {setImageLoading} from '@EmailMakerReact/lib/email-maker-media-library';
import {updateMsOutlookStyles} from '@EmailMakerReact/lib/email-maker-compatibility-library';
import {getEmailMakerPollingEventId} from '@EmailMakerReact/lib/email-maker-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

/** Design preview generation for the React email maker: loads a PMW design component's preview and polls until the generated graphic is ready. */

/**
 * Exported for the AI design-morph flow in `email-maker-ai-library.ts`.
 * Deprecated: will be removed once multipages are fully enabled — use {@link loadDesignPageForId}.
 */
export async function loadDesignForId(editor: Editor, component: Component, id: string): Promise<void> {
  const myDesignComponent = convertImageToDesignComponentIfNeeded(editor, component);

  myDesignComponent.set(EMAIL_MAKER_PROPS.DESIGN_ID, id);

  await loadDesignPreviewForEndpoint(editor, myDesignComponent, 'emailmaker/asyncGetPreviewForDesign', {idPoster: id}, (imgData) => {
    if (!isPreviewReady(imgData)) {
      pollForPreviewGraphicLegacy(editor, myDesignComponent, id);
    }

    setDesignSrcData(editor, myDesignComponent, {
      src: imgData.url,
      isPlaceholder: !isPreviewReady(imgData),
    });
  });
}

/** Multipage-aware design preview loading; exported for the AI design-morph flow. */
export async function loadDesignPageForId(editor: Editor, component: Component, pageData: PosterPageHashedIdInfo): Promise<void> {
  const myDesignComponent = convertImageToDesignComponentIfNeeded(editor, component);

  myDesignComponent.set(EMAIL_MAKER_PROPS.DESIGN_ID, pageData.posterHashedId);
  myDesignComponent.set(EMAIL_MAKER_PROPS.DESIGN_PAGE_ID, pageData.pageHashedId);

  await loadDesignPreviewForEndpoint(
    editor,
    myDesignComponent,
    'emailmaker/asyncGetPreviewForDesignV2',
    {posterHashedId: pageData.posterHashedId, pageHashedId: pageData.pageHashedId},
    (imgData) => {
      setDesignSrcData(editor, myDesignComponent, {
        src: imgData.url,
        isPlaceholder: !isPreviewReady(imgData),
      });

      if (!isPreviewReady(imgData)) {
        pollForPreviewGraphic(editor, myDesignComponent, pageData);
      }
    }
  );
}

/**
 * Shared loader for `loadDesignForId` / `loadDesignPageForId`: sets image loading, fetches the
 * preview endpoint, applies creator/premium/internal props, then delegates src+poll ordering to
 * `applyResult` (the two callers differ only in that ordering and their endpoint+params).
 */
async function loadDesignPreviewForEndpoint(
  editor: Editor,
  component: Component,
  endpoint: string,
  params: Record<string, string>,
  applyResult: (imgData: AsyncGetPreviewSuccessResponse) => void
): Promise<void> {
  setImageLoading(editor, {component, loading: true});

  try {
    const imgData = (await window.PMW.writeLocal(endpoint, params)) as AsyncGetPreviewSuccessResponse;

    applyDesignPreviewResponse(component, imgData);
    applyResult(imgData);
  } catch (errorResponse) {
    setImageLoading(editor, {component, loading: false});
    openDesignPreviewErrorModal(errorResponse);
  }
}

/** Applies the creator/premium/internal props from an asyncGetPreview response onto the component. */
function applyDesignPreviewResponse(component: Component, imgData: AsyncGetPreviewSuccessResponse): void {
  component.set(EMAIL_MAKER_PROPS.DESIGN_CREATOR_ID, imgData.creatorID);
  component.set(EMAIL_MAKER_PROPS.IS_PREMIUM_DESIGN, imgData.isPremium);
  component.set(EMAIL_MAKER_PROPS.IS_INTERNAL, imgData.isInternal);
}

/** Shared error mapping of the legacy loadDesignForID / loadDesignPageForID catch blocks. */
function openDesignPreviewErrorModal(errorResponse: unknown): void {
  if (!errorResponse || typeof errorResponse !== 'object' || !('code' in errorResponse)) {
    openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
    return;
  }

  const responseCode = getEnumMember(AsyncGetPreviewErrorCodes, String(errorResponse.code));
  if (!responseCode) {
    openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
    return;
  }

  switch (responseCode) {
    case AsyncGetPreviewErrorCodes.POSTER_NOT_PUBLIC:
      openErrorModal({message: window.i18next.t('pmwjs_design_not_accessible')});
      break;
    case AsyncGetPreviewErrorCodes.POSTER_NOT_FOUND:
      openErrorModal({message: window.i18next.t('pmwjs_emailmaker_unable_to_copy_design')});
      break;
    case AsyncGetPreviewErrorCodes.ERROR:
      openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
      break;
  }
}

function convertImageToDesignComponentIfNeeded(editor: Editor, component: Component): Component {
  return convertComponentIfNeeded(editor, component, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE, EMAIL_MAKER_BLOCKS.ID_DESIGN);
}

function isPreviewReady(data: AsyncGetPreviewSuccessResponse): boolean {
  return parseInt(data.isReady, 10) === 1;
}

/** Deprecated: removed once multipages are fully enabled — use {@link pollForPreviewGraphic}. */
function pollForPreviewGraphicLegacy(editor: Editor, component: Component, designId: string): void {
  void pollPreviewGraphicAndApply(
    editor,
    component,
    async (): Promise<void> => {
      return runPreviewGraphicKickoff('emailmarketing/preparePreviewGraphic', {
        designId,
        removeWatermark: true,
      });
    },
    async (): Promise<unknown> => {
      return window.PMW.readLocal('emailmarketing/checkPreviewGenerationStatus', {
        designId,
      });
    }
  );
}

/** Polls until the generated preview graphic is ready, then swaps the src in. */
function pollForPreviewGraphic(editor: Editor, component: Component, pageData: PosterPageHashedIdInfo): void {
  void pollPreviewGraphicAndApply(
    editor,
    component,
    async (): Promise<void> => {
      return runPreviewGraphicKickoff('emailmarketing/preparePreviewGraphicV2', {
        designId: pageData.posterHashedId,
        pageId: pageData.pageHashedId,
        removeWatermark: true,
      });
    },
    async (): Promise<unknown> => {
      return window.PMW.readLocal('emailmarketing/checkPreviewGenerationStatusV2', {
        designId: pageData.posterHashedId,
        pageId: pageData.pageHashedId,
      });
    }
  );
}

async function pollPreviewGraphicAndApply(editor: Editor, component: Component, kickoff: () => Promise<void>, poll: () => Promise<unknown>): Promise<void> {
  try {
    const url = (await window.PMW.pollingAjaxCallAsync(kickoff, poll, undefined, getEmailMakerPollingEventId(editor))) as string | undefined;
    if (!url) {
      onPreviewGenerationError(editor, component);
      return;
    }

    setDesignSrcData(editor, component, {src: url, isPlaceholder: false});
  } catch {
    onPreviewGenerationError(editor, component);
  }
}

/** An empty response body means success; only an explicit error envelope aborts the polling loop. */
async function runPreviewGraphicKickoff(uri: string, data: Record<string, unknown>): Promise<void> {
  const response = (await window.PMW.write(window.PMW.util.site_url(uri), data)) as {status?: string; message?: string} | string | undefined;
  if (response && typeof response === 'object' && response.status === 'error') {
    throw new Error(response.message ?? '');
  }
}

function onPreviewGenerationError(editor: Editor, component: Component): void {
  logEmailMakerError('onPreviewGenerationError: preview generation failed', undefined);
  component.set(EMAIL_MAKER_PROPS.DESIGN_MODIFIED, false);
  setImageLoading(editor, {component, loading: false});
}

/** Applies the preview src and clears the loading/modified state (skipped if it is a placeholder). */
function setDesignSrcData(editor: Editor, component: Component, data: {src: string; isPlaceholder: boolean}): void {
  try {
    updateMsOutlookStyles(editor, component);

    if (data.isPlaceholder) {
      return;
    }

    component.set('src', data.src);
    editor.getModel().skip(() => {
      component.addAttributes({class: component.getClasses() as string[]}, {});
    });
    component.set(EMAIL_MAKER_PROPS.DESIGN_MODIFIED, false);
    setImageLoading(editor, {component, loading: false});
  } catch (e) {
    logEmailMakerError('setDesignSrcData: failed to apply design preview', e);
  }
}
